import React, { useMemo } from 'react'
import { useCurrent, useFetch } from '../hooks'
import { CardHeaderWithActions, MwButton } from '../components/ui';
import TicketsList from '../components/tickets/TicketsList';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getStatusAppearance, getStatusLabel, normalizeStatusValue, normalizeSupportSettings } from '../constants/ticket';
import { applyTicketDateParams } from '../utils/ticketDateRange';

const TicketsManage = () => {
    const navigate = useNavigate()
    const params = useParams()
    const [searchParams] = useSearchParams();
    const { user, currentLangId } = useCurrent();
    const [t] = useTranslation('global')
    const filterParams = useMemo(() => {
        const apiParams = new URLSearchParams();
        ['section', 'product', 'priority', 'tenantId', 'visibility', 'assignee', 'q'].forEach((key) => {
            const value = String(searchParams.get(key) || '').trim();
            if (value && value !== 'all') apiParams.set(key, value);
        });
        // فلتر التاريخ (الافتراضي هذا الشهر) — بيأثر على القائمة وعدّادات الحالات مع بعض.
        return applyTicketDateParams(apiParams, searchParams);
    }, [searchParams]);
    const ticketsUrl = useMemo(() => {
        const apiParams = new URLSearchParams(filterParams);
        apiParams.set('limit', '12');
        apiParams.set('page', searchParams.get('page') || '1');
        ['status', 'statusGroup'].forEach((key) => {
            const value = String(searchParams.get(key) || '').trim();
            if (value && value !== 'all') apiParams.set(key, value);
        });
        return `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin?${apiParams.toString()}`;
    }, [filterParams, searchParams]);
    // عدّادات الحالات من السيرفر بنفس الفلاتر (من غير الحالة نفسها) — نفس مصدر لوحة التحكم.
    const statsUrl = `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/stats?${filterParams.toString()}`;
    const {data:resultTickets,loading:loadingTickets,refreshHandler:refreshTicketsList} = useFetch(ticketsUrl);
    const {data:statsResult,refreshHandler:refreshStats} = useFetch(statsUrl);
    const refreshTickets = () => {
        refreshTicketsList?.();
        refreshStats?.();
    };
    const {data:supportSettingsResult} = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`);
    const {data:statusesResult} = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/statuses/admin`);
    const {data:tenantsResult} = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/tenants/getAll`);
    const supportSettingsBase = normalizeSupportSettings(supportSettingsResult);
    const supportSettings = {
        ...supportSettingsBase,
        statuses: normalizeSupportSettings({ data: { statuses: statusesResult?.data || [] } }).statuses
    };
    const tenants = Array.isArray(tenantsResult?.data) ? tenantsResult.data : [];
    const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';
    const scopedTenants = useMemo(() => {
        if (user?.role === 'admin') return tenants;
        if (isTruthyFlag(user?.supportAdminEnabled) && isTruthyFlag(user?.supportSuperAdminEnabled)) return tenants;
        if (!isTruthyFlag(user?.supportAdminEnabled) || !isTruthyFlag(user?.supportTenantAdminEnabled)) return tenants;

        const scope = String(user?.supportTenantScope || 'own');
        if (scope === 'all') return tenants;

        if (scope === 'selected') {
            const allowedIds = Array.isArray(user?.supportTenantIds) ? user.supportTenantIds.map(String) : [];
            return tenants.filter((tenant) => allowedIds.includes(String(tenant?._id)));
        }

        const ownTenantId = user?.tenant_id?._id || user?.tenant_id || null;
        if (!ownTenantId) return [];
        return tenants.filter((tenant) => String(tenant?._id) === String(ownTenantId));
    }, [tenants, user]);

    const activeStatusValue = useMemo(() => normalizeStatusValue(searchParams.get('status') || 'all'), [searchParams]);
    const activeStatusLabel = useMemo(() => {
        if (!activeStatusValue || activeStatusValue === 'all') return '';
        return getStatusLabel(supportSettings?.statuses, activeStatusValue, currentLangId);
    }, [activeStatusValue, currentLangId, supportSettings?.statuses]);
    const activeStatusAppearance = useMemo(() => {
        if (!activeStatusValue || activeStatusValue === 'all') return null;
        return getStatusAppearance(supportSettings?.statuses, activeStatusValue);
    }, [activeStatusValue, supportSettings?.statuses]);

    return (
    <div className="admin-page">
        <CardHeaderWithActions
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span>{t('support.tickets.listTitle')}</span>
                    {activeStatusLabel ? (
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 10px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 800,
                            background: activeStatusAppearance?.bg || '#e2e8f0',
                            color: activeStatusAppearance?.color || '#475569'
                        }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: activeStatusAppearance?.dot || activeStatusAppearance?.color || '#94a3b8' }} />
                            {activeStatusLabel}
                        </span>
                    ) : null}
                </div>
            }
        >
            <MwButton type='saveBtn' onClick={()=>navigate(`/${params.tenant}/tickets/add`)} >{t('support.menu.createTicket')}</MwButton>
        </CardHeaderWithActions>
        <div className="admin-panel p-3 md:p-4">
            <TicketsList data={resultTickets} statusStats={statsResult?.data || null} loading={loadingTickets} supportSettings={supportSettings} currentUser={user} tenants={scopedTenants} onRefresh={refreshTickets}/>
        </div>
    </div>
    )
}

export default TicketsManage
